import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';

import { useLocation } from 'reservations/account/providers/ConfigProvider';
import { BENTOBOX_BOOKING_ENDPOINT_V2 } from 'reservations/base/constants/endpoints';
import { HTTP_STATUS_CODE_402_PAYMENT_REQUIRED } from 'reservations/base/constants/httpStatusCodes';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useModal } from 'reservations/base/providers/ModalProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import {
  type BookingMutationResponse,
  BookingMutationResponseSchema
} from 'reservations/booking/types';
import { useDinerAccount } from 'reservations/dinerAccount/hooks/useDinerAccount';
import type { BookingFormSchema } from 'reservations/form/components/Form';
import { cleanCreateBookingFormData } from 'reservations/form/helpers/cleanBookingFormData';
import { restClient } from 'utility/restClient';
import type { RequestError } from 'utility/restClient';

export const useCreateBookingMutation = () => {
  const { slug, id: locationId } = useLocation();
  const queryClient = useQueryClient();
  const { handleNavigation } = useCustomRouter();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const { setBeginAuthParams } = useDinerAccount();
  const [availability, email] = useWatch({
    name: ['availability', 'email']
  });
  const { close, setModalContext } = useModal();

  return useMutation<
    BookingMutationResponse,
    RequestError,
    Record<string, any>
  >({
    mutationKey: ['CREATE_BOOKING'],
    mutationFn: async formData => {
      const cleanedFormData = cleanCreateBookingFormData(
        formData as BookingFormSchema,
        locationId as string
      );
      const endpoint = buildBentoboxApiEndpoint(BENTOBOX_BOOKING_ENDPOINT_V2);

      const response = await restClient.post(endpoint, {
        data: Object.fromEntries(cleanedFormData)
      });

      try {
        return BookingMutationResponseSchema.parse(response);
      } catch (err) {
        console.error('useCreateBookingMutation: ', err);
        throw err;
      }
    },
    onSuccess: data => {
      close();
      handleNavigation(
        `/locations/${slug}/booking/${data.booking_id}?visitId=${data.visit_id}`
      );
    },
    onError: error => {
      if (error.code === HTTP_STATUS_CODE_402_PAYMENT_REQUIRED) {
        setModalContext({ cardDeclinedError: true });
        return;
      }

      if (error.message === 'Account diner already exists') {
        setBeginAuthParams(email);
      }

      if (error.code === NEXTABLE_ERROR_CODE.NO_AVAILABILITY) {
        enqueueErrorSnackbar(
          'CREATE_BOOKING_ERROR_SNACKBAR',
          `${availability} is no longer available. Please select a new time.`
        );
        queryClient.invalidateQueries({
          queryKey: ['bentoboxReservations', 'availabilities']
        });
      }
    }
  });
};
